import React, { useState, useEffect } from 'react';
import { ShieldCheck, ChevronDown, Check, Info } from 'lucide-react';
import { getStoredConsent, saveConsent, initTracking } from '../lib/tracking';
import { app } from '../firebase';

export default function CookieBanner() {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState({
        analytics: true
    });

    useEffect(() => {
        const stored = getStoredConsent();
        if (!stored) {
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        const fullConsent = { analytics: true };
        saveConsent(fullConsent);
        initTracking(fullConsent, app);
        setIsVisible(false);
    };

    const handleRefuseAll = () => {
        const noConsent = { analytics: false };
        saveConsent(noConsent);
        setIsVisible(false);
    };

    const handleSaveCustom = () => {
        saveConsent(consent);
        initTracking(consent, app);
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 animate-fade-in-up">
            <div className="max-w-4xl mx-auto bg-[#1C1C1C] border border-white/10 rounded-[2rem] shadow-2xl shadow-black/50 overflow-hidden backdrop-blur-xl text-white">
                <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-8 items-start">
                        {/* Icon & Description */}
                        <div className="bg-[#F9D965]/10 p-4 rounded-2xl shrink-0 hidden md:block">
                            <ShieldCheck className="w-8 h-8 text-[#F9D965]" />
                        </div>

                        <div className="flex-1 space-y-4">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-[#F9D965] md:hidden" />
                                Respect de votre vie privée
                            </h3>
                            <p className="text-[#F5F3EE]/70 text-sm leading-relaxed">
                                Chez SuperAsso, nous utilisons des outils de mesure pour comprendre comment vous utilisez la plateforme et à améliorer votre expérience.
                                <span className="hidden sm:inline"> Nous ne vendons jamais vos données et respectons strictement le RGPD.</span>
                            </p>

                            {/* Choix rapides */}
                            <div className="flex flex-wrap gap-3 pt-2">
                                <button
                                    onClick={handleAcceptAll}
                                    className="px-6 py-2.5 bg-[#F9D965] text-[#24574E] rounded-xl text-sm font-black hover:scale-105 transition-all active:scale-95 shadow-lg shadow-[#F9D965]/10"
                                >
                                    Tout Accepter
                                </button>
                                <button
                                    onClick={handleRefuseAll}
                                    className="px-6 py-2.5 bg-white/5 text-white/60 hover:text-white rounded-xl text-sm font-bold border border-white/5 transition-all"
                                >
                                    Tout Refuser
                                </button>
                                <button
                                    onClick={() => setShowSettings(!showSettings)}
                                    className="px-6 py-2.5 flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold transition-all"
                                >
                                    Personnaliser
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Paramètres détaillés */}
                    {showSettings && (
                        <div className="mt-8 pt-8 border-t border-white/5 space-y-6 animate-fade-in">
                            <div className="max-w-md mx-auto">
                                <div
                                    className={`p-5 rounded-2xl border transition-all cursor-pointer ${consent.analytics ? 'bg-[#5BC0AF]/10 border-[#5BC0AF]/20' : 'bg-white/5 border-white/5 opacity-60'}`}
                                    onClick={() => setConsent({ ...consent, analytics: !consent.analytics })}
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="p-2 bg-white/5 rounded-lg">
                                            <ShieldCheck className={`w-5 h-5 ${consent.analytics ? 'text-[#5BC0AF]' : 'text-white/40'}`} />
                                        </div>
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${consent.analytics ? 'bg-[#5BC0AF] border-[#5BC0AF]' : 'border-white/20'}`}>
                                            {consent.analytics && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                    </div>
                                    <h4 className="font-bold text-white text-sm mb-1">Mesure d'audience</h4>
                                    <p className="text-[11px] text-[#F5F3EE]/40 leading-tight">Statistiques anonymes via Firebase pour suivre l'utilisation globale.</p>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={handleSaveCustom}
                                    className="px-8 py-3 bg-white text-[#24574E] rounded-xl text-sm font-black hover:scale-105 transition-all shadow-xl"
                                >
                                    Enregistrer mes choix
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
